import React, { useState } from 'react';
import { Event } from '../types';
import MapView from './MapView';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Tag } from 'lucide-react';
import { getEventImage } from '../services/eventImageService';

interface ExploreMapViewProps {
  events: Event[];
  userLocation?: { lat: number; lng: number };
  currentUserId?: string;
  onEventClick: (event: Event) => void;
  acceptedEventIds?: string[];
}

const ExploreMapView: React.FC<ExploreMapViewProps> = ({
  events,
  userLocation,
  currentUserId,
  onEventClick,
  acceptedEventIds = [],
}) => {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const handleEventSelect = (event: Event) => {
    // Show the preview card
    setSelectedEvent(event);
  };

  const handleCardClick = () => {
    if (selectedEvent) {
      onEventClick(selectedEvent);
    }
  };

  const handleClosePreview = () => {
    setSelectedEvent(null);
  };

  return (
    <div className="relative w-full h-full">
      {/* Map */}
      <MapView
        events={events}
        selectedEvent={selectedEvent}
        userLocation={userLocation}
        currentUserId={currentUserId}
        onEventSelect={handleEventSelect}
        interactive={true}
        showUserLocation={true}
        acceptedEventIds={acceptedEventIds}
        mapContext="explore"
        showRadiusCircles={false}
        className="w-full h-full"
      />

      {/* Preview Card - Matches EventCard Style */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.4, bounce: 0.25 }}
            className="absolute top-8 left-4 right-4 z-20 max-w-sm mx-auto"
          >
            {/* Card matching EventCard design */}
            <div className="relative">
              <div className="relative bg-theme-cards rounded-2xl overflow-hidden shadow-2xl ring-2 ring-white/20">
                {/* Close button - Big and Clear */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClosePreview();
                  }}
                  className="absolute top-3 right-3 z-30 flex items-center justify-center w-10 h-10 rounded-full bg-black/80 hover:bg-black transition-all shadow-xl hover:scale-110 backdrop-blur-sm border-2 border-white/20"
                  aria-label="Close preview"
                >
                  <X className="w-5 h-5 text-white stroke-[3]" />
                </button>

                {/* Event Preview */}
                <div onClick={handleCardClick} className="cursor-pointer">
                  {/* Image with overlay */}
                  <div className="relative aspect-[4/3]">
                    <img
                      src={getEventImage(selectedEvent)}
                      alt={selectedEvent.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />

                    {/* Content overlay at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 z-20 text-white p-4 bg-gradient-to-t from-black/90 to-transparent">
                      <h3 className="font-bold text-lg leading-tight line-clamp-2 mb-2 drop-shadow-lg">
                        {selectedEvent.title}
                      </h3>

                      {/* Date and Location info */}
                      <div className="space-y-1 text-sm text-gray-200 mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-theme-primary flex-shrink-0" />
                          <span className="font-medium">
                            {new Date(selectedEvent.date).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>

                      {/* Category badge */}
                      <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-primary-100 text-contrast font-semibold shadow-md">
                          <Tag className="w-3.5 h-3.5" />
                          {selectedEvent.category}
                        </span>
                      </div>

                      {/* CTA Button */}
                      <button className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white py-3 rounded-full font-bold text-base hover:shadow-xl hover:scale-[1.02] transition-all shadow-lg">
                        View Full Details →
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pointer triangle pointing down to the pin */}
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[16px] border-t-[var(--theme-cards)] drop-shadow-xl"
                   style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No events message */}
      {events.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-theme-bg-secondary p-8 rounded-2xl border border-theme-border-primary text-center max-w-md">
            <p className="text-theme-text-secondary">
              No Vybes to show on the map. Try adjusting your filters.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreMapView;
